// WHAT: Organism component for managing MiracleFlow affirmations with CRUD operations.
// WHY:  Provides comprehensive interface for creating, viewing, editing, and deleting affirmations for morning and evening sessions.
// DOMAIN: Productivity → MiracleFlow
// DEPENDENCIES: React, @tracq/frontend-shared/utils, ../molecules/AffirmationCard, @/components/ui, ../../types, ../../utils/api-client, lucide-react
// RISKS:
//   - WARN: API calls (affirmationApi) must exist; failures will show error messages.
//   - NOTE: Delete confirmation uses browser confirm dialog; may not match app styling.
//   - WARN: Editing state is managed locally; unsaved changes may be lost on tab switch.

import { Sun, Moon, Plus, AlertTriangle, Info, ChevronLeft } from "lucide-react";
import React, { useState, useEffect } from "react";

import { Button, Badge, Card, Stack, Heading } from "@/components/ui";
import { cn } from "@/shared/utils";

import { type Affirmation } from "../../types";
import { affirmationApi } from "../../utils/api-client";
import { AffirmationCard } from "../molecules";

export interface AffirmationManagerProperties {
  className?: string;
  initialTab?: "evening" | "morning";
  onBack?: () => void;
}

/**
 * WHAT:
 *   Organism component for managing MiracleFlow affirmations with CRUD operations.
 *
 * WHY:
 *   Provides users with comprehensive interface for creating, viewing, editing, and deleting
 *   affirmations for morning and evening sessions. Enables personalization of productivity routines.
 *
 * PROPS:
 *   - onBack?: () => void - Optional callback to navigate back
 *   - className?: string - Optional additional CSS classes
 *   - initialTab?: 'morning' | 'evening' - Initial active tab (default: 'morning')
 *
 * BEHAVIOR:
 *   - Loads morning and evening affirmations on mount
 *   - Manages active tab state (morning/evening)
 *   - Displays affirmation cards in grid layout
 *   - Provides create form for new affirmations
 *   - Supports editing existing affirmations
 *   - Handles deletion with confirmation dialog
 *   - Shows loading and error states
 *   - Refreshes data after create/update/delete operations
 *
 * RISKS:
 *   - WARN: API calls (affirmationApi) must exist; failures will show error messages.
 *   - NOTE: Delete confirmation uses browser confirm dialog; may not match app styling.
 *   - WARN: Editing state is managed locally; unsaved changes may be lost on tab switch.
 *   - NOTE: Error messages are generic; detailed error information may be lost.
 *
 * @param root0
 *
 * @param root0.onBack
 *
 * @param root0.className
 *
 * @param root0.initialTab
 *
 * @returns JSX.Element
 *
 */
export const AffirmationManager: React.FC<AffirmationManagerProperties> = ({
  className,
  initialTab,
  onBack,
}) => {
  const [activeTab, setActiveTab] = useState<"evening" | "morning">(initialTab ?? "morning");
  const [morningAffirmations, setMorningAffirmations] = useState<Affirmation[]>([]);
  const [eveningAffirmations, setEveningAffirmations] = useState<Affirmation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [editingId, setEditingId] = useState<null | string>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  /**
   * WHAT:
   *   Effect hook to load affirmations on component mount.
   *
   * WHY:
   *   Ensures affirmations are displayed immediately when component is rendered.
   *
   * SIDE EFFECTS:
   *   - Calls loadAffirmations to fetch data
   */
  useEffect(() => {
    // NOTE: Wrap async call in void to avoid floating promise while preserving fire-and-forget behavior
    void loadAffirmations();
  }, []);

  /**
   * WHAT:
   *   Loads morning and evening affirmations from API.
   *
   * WHY:
   *   Retrieves current affirmations for both session types to display in the manager.
   *
   * SIDE EFFECTS:
   *   - Sets isLoading to true during fetch
   *   - Clears error state
   *   - Calls affirmationApi.getMorningAffirmations and getEveningAffirmations in parallel
   *   - Updates morningAffirmations and eveningAffirmations states
   *   - Sets error state if loading fails
   *   - Sets isLoading to false on completion
   *
   * RISKS:
   *   - WARN: API calls must exist; failures will show error messages.
   *   - NOTE: Both API calls happen in parallel; one failure affects the entire load.
   */
  const loadAffirmations = async () => {
    try {
      setIsLoading(true);
      setError("");

      const [morning, evening] = await Promise.all([
        affirmationApi.getMorningAffirmations(),
        affirmationApi.getEveningAffirmations(),
      ]);

      setMorningAffirmations(morning);
      setEveningAffirmations(evening);
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : "Failed to load affirmations");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * WHAT:
   *   Creates a new affirmation for the active tab.
   *
   * WHY:
   *   Allows users to add new affirmations to their collection.
   *
   * INPUTS:
   *   - text: string - Affirmation text content
   *
   * SIDE EFFECTS:
   *   - Calls affirmationApi.createAffirmation with text and activeTab
   *   - Reloads affirmations after successful creation
   *   - Hides create form
   *   - Sets error state if creation fails
   *
   * RISKS:
   *   - WARN: API call must exist; failures will show error messages.
   *   - NOTE: Validation is handled by AffirmationCard; ensure it matches API requirements.
   *
   * @param text
   *
   */
  const handleCreateAffirmation = async (text: string) => {
    try {
      await affirmationApi.createAffirmation(text, activeTab);
      await loadAffirmations();
      setShowCreateForm(false);
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : "Failed to create affirmation");
    }
  };

  /**
   * WHAT:
   *   Updates an existing affirmation.
   *
   * WHY:
   *   Allows users to modify affirmation text.
   *
   * INPUTS:
   *   - id: string - Affirmation ID
   *   - text: string - Updated affirmation text
   *
   * SIDE EFFECTS:
   *   - Calls affirmationApi.updateAffirmation with id and text
   *   - Reloads affirmations after successful update
   *   - Clears editingId state
   *   - Sets error state if update fails
   *
   * RISKS:
   *   - WARN: API call must exist; failures will show error messages.
   *   - NOTE: Editing state is cleared on success; ensure user feedback is provided.
   *
   * @param id
   *
   * @param text
   *
   */
  const handleUpdateAffirmation = async (id: string, text: string) => {
    try {
      await affirmationApi.updateAffirmation(id, text);
      await loadAffirmations();
      setEditingId(null);
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : "Failed to update affirmation");
    }
  };

  /**
   * WHAT:
   *   Deletes an affirmation after user confirmation.
   *
   * WHY:
   *   Allows users to remove affirmations they no longer need.
   *
   * INPUTS:
   *   - id: string - Affirmation ID to delete
   *
   * SIDE EFFECTS:
   *   - Shows browser confirm dialog
   *   - Calls affirmationApi.deleteAffirmation if confirmed
   *   - Reloads affirmations after successful deletion
   *   - Sets error state if deletion fails
   *
   * RISKS:
   *   - WARN: Delete operation is permanent; confirmation dialog is required.
   *   - NOTE: Uses browser confirm dialog; may not match app styling.
   *   - WARN: API call must exist; failures will show error messages.
   *
   * @param id
   *
   */
  const handleDeleteAffirmation = async (id: string) => {
    if (!confirm("Are you sure you want to delete this affirmation?")) {
      return;
    }

    try {
      await affirmationApi.deleteAffirmation(id);
      await loadAffirmations();
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : "Failed to delete affirmation");
    }
  };

  /**
   * WHAT:
   *   Returns affirmations for the currently active tab.
   *
   * WHY:
   *   Provides filtered affirmations based on active tab selection.
   *
   * OUTPUT:
   *   - Affirmation[] - Array of affirmations for active tab
   *
   * @returns Array of affirmations for active tab
   *
   */
  const getCurrentAffirmations = (): Affirmation[] => {
    return activeTab === "morning" ? morningAffirmations : eveningAffirmations;
  };

  const renderHeader = () => (
    <Card className="bg-[#1E293B] border-[#334155]" padding="lg" variant="default">
      <Stack direction="col" gap={6}>
        {/* Back Button & Title */}
        <Stack align="center" direction="row" gap={4}>
          {onBack ? (
            <Button
              className="border-[#334155] text-[#CBD5E1] hover:bg-[#334155] hover:border-tracq-bright-blue/50"
              onClick={onBack}
              size="sm"
              variant="outline"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          ) : null}

          <Stack direction="col" gap={1}>
            <Heading className="text-[#F1F5F9] font-bold" level="h2" size="xl">
              Affirmation Manager
            </Heading>
            <p className="text-[#94A3B8]">Create and manage your positive affirmations</p>
          </Stack>
        </Stack>

        {/* Tabs */}
        <div className="flex space-x-2 bg-[#0F172A] p-1 rounded-tracq border border-[#334155] w-fit">
          <button
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-tracq text-sm font-medium transition-all duration-200",
              activeTab === "morning"
                ? "bg-gradient-to-r from-tracq-deep-blue to-tracq-bright-blue text-white shadow-tracq-card"
                : "text-[#94A3B8] hover:text-[#CBD5E1] hover:bg-[#334155]",
            )}
            onClick={() => {
              setActiveTab("morning");
            }}
          >
            <Sun className="w-4 h-4" />
            Morning
            <Badge
              className={cn(
                activeTab === "morning"
                  ? "bg-white/20 text-white border-white/30"
                  : "bg-[#334155] text-[#94A3B8] border-[#475569]",
              )}
              size="sm"
              variant="default"
            >
              {morningAffirmations.length}
            </Badge>
          </button>

          <button
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-tracq text-sm font-medium transition-all duration-200",
              activeTab === "evening"
                ? "bg-gradient-to-r from-tracq-deep-orange to-tracq-light-orange text-white shadow-tracq-card"
                : "text-[#94A3B8] hover:text-[#CBD5E1] hover:bg-[#334155]",
            )}
            onClick={() => {
              setActiveTab("evening");
            }}
          >
            <Moon className="w-4 h-4" />
            Evening
            <Badge
              className={cn(
                activeTab === "evening"
                  ? "bg-white/20 text-white border-white/30"
                  : "bg-[#334155] text-[#94A3B8] border-[#475569]",
              )}
              size="sm"
              variant="default"
            >
              {eveningAffirmations.length}
            </Badge>
          </button>
        </div>

        {/* Actions */}
        <Stack direction="row" gap={3}>
          <Button
            className={cn(
              activeTab === "morning"
                ? "bg-gradient-to-r from-tracq-deep-blue to-tracq-bright-blue hover:from-tracq-deep-blue/90 hover:to-tracq-bright-blue/90"
                : "bg-gradient-to-r from-tracq-deep-orange to-tracq-light-orange hover:from-tracq-deep-orange/90 hover:to-tracq-light-orange/90",
            )}
            onClick={() => {
              setShowCreateForm(true);
            }}
            variant="primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Affirmation
          </Button>
        </Stack>
      </Stack>
    </Card>
  );

  const renderAffirmationsList = () => {
    const affirmations = getCurrentAffirmations();

    if (affirmations.length === 0) {
      return (
        <Card className="bg-[#1E293B] border-[#334155]" padding="lg" variant="default">
          <Stack align="center" direction="col" gap={4}>
            <div
              className={cn(
                "flex items-center justify-center w-16 h-16 rounded-tracq shadow-tracq-card",
                activeTab === "morning"
                  ? "bg-gradient-to-br from-tracq-deep-blue/20 to-tracq-bright-blue/20 border border-tracq-bright-blue/30"
                  : "bg-gradient-to-br from-tracq-deep-orange/20 to-tracq-light-orange/20 border border-tracq-light-orange/30",
              )}
            >
              {activeTab === "morning" ? (
                <Sun className="w-8 h-8 text-tracq-bright-blue" />
              ) : (
                <Moon className="w-8 h-8 text-tracq-light-orange" />
              )}
            </div>
            <Stack align="center" direction="col" gap={2}>
              <Heading className="text-[#F1F5F9] font-bold" level="h3" size="lg">
                No {activeTab} affirmations yet
              </Heading>
              <p className="text-[#94A3B8] text-center">
                Create your first affirmation to get started
              </p>
            </Stack>
            <Button
              className={cn(
                activeTab === "morning"
                  ? "bg-gradient-to-r from-tracq-deep-blue to-tracq-bright-blue hover:from-tracq-deep-blue/90 hover:to-tracq-bright-blue/90"
                  : "bg-gradient-to-r from-tracq-deep-orange to-tracq-light-orange hover:from-tracq-deep-orange/90 hover:to-tracq-light-orange/90",
              )}
              onClick={() => {
                setShowCreateForm(true);
              }}
              variant="primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Affirmation
            </Button>
          </Stack>
        </Card>
      );
    }

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {affirmations.map((affirmation) => (
          <AffirmationCard
            affirmation={affirmation}
            key={affirmation.id}
            mode={editingId === affirmation.id ? "edit" : "view"}
            onCancel={() => {
              setEditingId(null);
            }}
            onDelete={() => {
              void handleDeleteAffirmation(affirmation.id);
            }}
            onEdit={() => {
              setEditingId(affirmation.id);
            }}
            onSave={(text) => {
              // NOTE: Explicitly ignore returned promise to satisfy @typescript-eslint/no-misused-promises
              void handleUpdateAffirmation(affirmation.id, text);
            }}
            type={activeTab}
          />
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card
        className={cn("bg-[#1E293B] border-[#334155]", className)}
        padding="lg"
        variant="default"
      >
        <Stack align="center" className="py-12" direction="col" gap={4}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tracq-bright-blue" />
          <p className="text-[#94A3B8]">Loading affirmations...</p>
        </Stack>
      </Card>
    );
  }

  return (
    <div className={cn("space-y-6", className)}>
      {renderHeader()}

      {/* Error Display */}
      {error ? (
        <Card className="bg-red-500/10 border-red-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <p className="text-red-300 text-sm">{error}</p>
          </Stack>
        </Card>
      ) : null}

      {/* Create Form */}
      {showCreateForm ? (
        <AffirmationCard
          mode="create"
          onCancel={() => {
            setShowCreateForm(false);
          }}
          onSave={(text) => {
            void handleCreateAffirmation(text);
          }}
          type={activeTab}
        />
      ) : null}

      {/* Affirmations List */}
      {renderAffirmationsList()}

      {/* Tips */}
      <Card className="bg-[#1E293B] border-[#334155]" padding="lg" variant="default">
        <Stack align="start" direction="row" gap={4}>
          <div className="flex items-center justify-center w-10 h-10 rounded-tracq bg-tracq-bright-blue/20 border border-tracq-bright-blue/30 flex-shrink-0">
            <Info className="w-5 h-5 text-tracq-bright-blue" />
          </div>
          <Stack direction="col" gap={2}>
            <Heading className="text-[#F1F5F9] font-semibold" level="h4" size="md">
              Affirmation Tips
            </Heading>
            <ul className="text-sm text-[#CBD5E1] space-y-2">
              <li>• Use present tense (&quot;I am&quot; instead of &quot;I will be&quot;)</li>
              <li>• Make them personal and meaningful to you</li>
              <li>• Keep them positive and empowering</li>
              <li>• Be specific about what you want to affirm</li>
              <li>• Review and update them regularly</li>
            </ul>
          </Stack>
        </Stack>
      </Card>
    </div>
  );
};
